/**
 * Return-to-post behavior: A*-based pathfinding with stuck detection.
 * Uses PathHelper's cached A* to navigate around obstacles (pillars, walls).
 * Four-stage escalation: budget bump → reverse path → nudge → relocate post.
 */
import { distance } from '../../utils/Math';
import type { Position } from '../../utils/Types';
import type { PathHelper } from '../PathHelper';
import { MovementResult, ZERO_MOVEMENT, STUCK_ESCALATION_INTERVAL_MS, DUTY_STUCK_PROGRESS, scaledAStarBudget, ESCALATED_A_STAR_BUDGET, PATH_WAYPOINT_THRESHOLD } from '../constants';
import { StuckDetector } from '../StuckDetector';

/** Mutable return-to-post state owned by DutySystem. */
export interface ReturnState {
  detector: StuckDetector;
  nudgeTarget: Position | null;
}

/** Create a fresh return state. */
export function createReturnState(): ReturnState {
  return {
    detector: new StuckDetector(STUCK_ESCALATION_INTERVAL_MS, {
      progressThresholdPx: DUTY_STUCK_PROGRESS,
      persistEscalation: true,
      requireMovementStall: true,
      maxEscalation: 4,
    }),
    nudgeTarget: null,
  };
}

/** Reset return-to-post tracking (called when near post). */
export function resetReturnState(state: ReturnState): void {
  state.detector.reset();
  state.nudgeTarget = null;
}

/**
 * Move back toward the duty post using A* pathfinding with stuck detection.
 *
 * Normal flow: PathHelper's cached A* (budget scaled by distance, recalculated
 * every 500ms) navigates around obstacles. Four-stage escalation if stuck:
 *   1. Bump A* budget to 2000 nodes
 *   2. Try reverse pathfinding (T→A reversed)
 *   3. Nudge to adjacent non-buffer tile
 *   4. Relocate duty post to current position (terminal)
 */
export function returnToPost(
  state: ReturnState,
  entityX: number, entityY: number,
  dutyX: number, dutyY: number,
  delta: number,
  moveSpeed: number,
  _groundLayer: Phaser.Tilemaps.TilemapLayer,
  pathHelper: PathHelper,
  onRelocate: (newX: number, newY: number) => void,
  logs: string[],
): MovementResult {
  const distToDuty = distance(entityX, entityY, dutyX, dutyY);

  // --- Active nudge: move toward adjacent tile instead of A* ---
  if (state.nudgeTarget) {
    const distToNudge = distance(entityX, entityY, state.nudgeTarget.x, state.nudgeTarget.y);
    if (distToNudge < PATH_WAYPOINT_THRESHOLD) {
      logs.push(`DUTY NUDGE arrived at (${Math.round(state.nudgeTarget.x)},${Math.round(state.nudgeTarget.y)})`);
      state.nudgeTarget = null;
      pathHelper.clearPath();
      // Fall through to normal A* pathfinding below
    } else {
      // Still moving toward nudge target — run stuck detection in case blocked
      const stuckResult = state.detector.update(delta, entityX, entityY, distToDuty);
      if (stuckResult.triggered >= state.detector.escalationLevel) {
        // Terminal while nudging — relocate
        logs.push(`DUTY STUCK: nudge blocked — relocating to (${Math.round(entityX)},${Math.round(entityY)})`);
        state.nudgeTarget = null;
        onRelocate(entityX, entityY);
        resetReturnState(state);
        return ZERO_MOVEMENT;
      }

      const ndx = state.nudgeTarget.x - entityX;
      const ndy = state.nudgeTarget.y - entityY;
      return {
        vx: (ndx / distToNudge) * moveSpeed,
        vy: (ndy / distToNudge) * moveSpeed,
        wantsAttack: false,
      };
    }
  }

  // --- Stuck detection ---
  // Timer accumulates when the entity is physically not moving (anchor-based).
  // Progress resets the timer but keeps the escalation level
  // (persistEscalation: true): once the enemy needed 2000 nodes to escape
  // a jam, keep using 2000 for the whole return trip until resetReturnState.
  const stuckResult = state.detector.update(delta, entityX, entityY, distToDuty);

  if (stuckResult.triggered === 4) {
    // Stage 4 (terminal): all strategies exhausted — relocate post
    logs.push(`DUTY UNREACHABLE at (${Math.round(dutyX)},${Math.round(dutyY)}) — relocating to (${Math.round(entityX)},${Math.round(entityY)})`);
    onRelocate(entityX, entityY);
    resetReturnState(state);
    return ZERO_MOVEMENT;
  }

  if (stuckResult.triggered === 3) {
    // Stage 3: nudge to adjacent non-buffer tile
    const nudge = pathHelper.findNudgeTarget(entityX, entityY, dutyX, dutyY);
    if (nudge) {
      logs.push(`DUTY STUCK: nudging to (${Math.round(nudge.x)},${Math.round(nudge.y)}) at dist=${Math.round(distToDuty)}`);
      state.nudgeTarget = nudge;
      pathHelper.clearPath();
    } else {
      // No valid nudge target — skip to terminal
      logs.push(`DUTY STUCK: no nudge target — relocating to (${Math.round(entityX)},${Math.round(entityY)})`);
      onRelocate(entityX, entityY);
      resetReturnState(state);
      return ZERO_MOVEMENT;
    }
  } else if (stuckResult.triggered === 2) {
    // Stage 2: try reverse pathfinding
    logs.push(`DUTY STUCK: trying reverse path at dist=${Math.round(distToDuty)}`);
    pathHelper.clearPath();
  } else if (stuckResult.triggered === 1) {
    // Stage 1: bump A* budget
    logs.push(`DUTY STUCK: escalating budget at dist=${Math.round(distToDuty)}`);
    pathHelper.clearPath();
  }

  // --- A* pathfinding ---
  // Budget exhaustion returns zero movement — stuck detection above handles
  // escalation and eventual relocation if truly unreachable.
  const level = state.detector.escalationLevel;
  const budget = level >= 1 ? ESCALATED_A_STAR_BUDGET : scaledAStarBudget(distToDuty);
  const reversed = level >= 2;
  return pathHelper.pathfindToward(
    entityX, entityY, dutyX, dutyY,
    delta, moveSpeed,
    undefined,
    budget,
    reversed,
  );
}
